import { Libraries, useLoadScript } from "@react-google-maps/api";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { getGoogleApiKey } from "@/helpers/apiHelpers";
import {
  FlightArrivalTerminal,
  extractAirportIata,
  findProviderTerminalPoi,
  isAirportPickup,
  pickupIsAtFlightTerminal,
} from "@/helpers/bookingHelpers";
import { FlightDetails } from "@/redux/apis/clientFlight/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { useListProviderPoisQuery } from "@/redux/apis/providerPoi/providerPoiApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  setPickupDatetime,
  setPickupFlightDelayMinutes,
  setPickupFlightLandingUsed,
  swapPickupForTerminal,
} from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectPickup,
  selectPickupDatetime,
  selectPickupFlightDelayMinutes,
  selectPickupFlightDerivedArrival,
  selectPickupFlightFrozenReason,
  selectPickupFlightLandingUsed,
  selectPickupFlightNumber,
  selectViewOnly,
} from "@/redux/slices/clientBooking/selectors";
import { defaultCountrySelector, operatorIdSelector } from "@/redux/slices/operator/selectors";
import {
  flightDateFromPickup,
  flightLandingUsedIso,
  flightPickupCommitValue,
  flightWillArrive,
  isFlightPickupFrozen,
  landingTime,
  pickupFromFlight,
  utcIsoToAirportIso,
} from "../flightPickup";
import { validatePickup } from "../pickupTime";
import { findGoogleTerminalPoi } from "../terminalPoi";
import { useFlightDetails } from "./useFlightDetails";

// Module-level so both the loader options and the empty-POI fallback stay referentially stable across
// renders — the terminal effect below has the POI list in its dependencies.
const libraries = ["places"] as Libraries;
const NO_POIS: PointsOfInterest[] = [];

/**
 * The pickup, the wait and the landing as ONE claim. The server verifies them together —
 * `landing + delay*60 === pickup`, on exact epoch equality — so they are built from a single landing in a
 * single call and committed together. Reading the landing again at the call site is how a pickup ends up
 * paired with an arrival it was not derived from, which is precisely the pair the server refuses.
 */
export interface RederivedFlightPickup {
  /** The value to commit to `clientBooking.pickupDatetime`. */
  value: string;
  /** The booked wait, carried through so the payload's `flight_landing_used` stays verifiable. */
  delayMinutes: number;
  /** `flight_landing_used` — the landing this pickup was built on. Null when the landing is unparseable. */
  landingUsed: string | null;
}

/**
 * Resolves the booking's flight for as long as the SHEET is open, and re-derives the pickup from the
 * latest landing when the save happens.
 *
 * ## The rule this exists for
 *
 * If someone opens a booking and saves it, the pickup is built from the latest flight data — whatever they
 * came in to change. Editing the drop-off, the vehicle type or the notes is not editing the flight, and
 * none of them should have to be the date field before the flight is re-checked.
 *
 * `DateTimePanel` mounts only while the Date/Time sub-panel is open (`TripDetailSubPanel.tsx:52` /
 * `:111`), so nothing resolves the flight for a client who came to change anything else. This hook is
 * what asks — and being the one that asks, it is also the one that answers on save.
 *
 * ## Everything here reads the COMMITTED draft
 *
 * Not one operand comes from panel-local state, because the panel is the thing that is not mounted. The
 * wait, the flight date and the flight number are the booked ones, which is also what makes the answer the
 * same whether or not the client happened to visit the date step.
 *
 * The lookup date is the FLIGHT's day, recovered by subtracting the booked wait from the booked pickup —
 * never the pickup's own day. A 23:45 landing with a 30-minute wait commits a pickup at 00:15 the next
 * day; asking Cirium about THAT day finds no flight, and no landing is sent for precisely the late-arrival
 * bookings the provenance exists for. See {@link flightDateFromPickup}.
 *
 * ## Deduped onto one cache entry
 *
 * The arguments are built the way `DateTimePanel` builds them — same `flightDateFromPickup`, same
 * `extractAirportIata` — so RTK Query serialises them identically and the two subscribers share one entry.
 * Extra subscribers cost extra requests only when they disagree about the argument.
 *
 * ## When it declines to re-derive
 *
 * The same operands the date panel weighs, minus the three that only exist while that panel is open (a
 * touched wait, a touched date, a newly typed flight number — each of those is still that panel's to
 * commit):
 *
 *   - VIEW-ONLY: never fetched, let alone re-derived. A closed booking's pickup is frozen while the flight
 *     keeps moving, so a recomputed one would visibly fail to add up on nearly every booking.
 *   - FROZEN (`at_gate` / `cancelled` / `diverted`): the engine will no longer move this pickup, so
 *     neither may the client — see {@link isFlightPickupFrozen}.
 *   - NOT ARRIVING: cancelled / diverted / unknown flights still carry a scheduled time, but pinning a
 *     pickup to it would be wrong.
 *   - NO STORED ARRIVAL: `pickupFlightDerivedArrival` is written in exactly one place,
 *     `mapBookingDetailToViewDraft`, so requiring it makes this an edit-path question by construction. On
 *     the create path there is simply the live flight and the pickup it produces, and nothing to compare.
 *   - NO DRIFT AT ALL: the live landing is the stored one to the second, so there is nothing to re-derive.
 *   - AN ELAPSED RE-DERIVATION: drift is the one operand the client cannot move. A flight that landed early
 *     enough puts landing + wait in the past, and re-deriving onto it would hand the client "Pickup time
 *     can't be in the past" on an edit they did not make, with no lever anywhere on the sheet to clear it.
 *     Declining keeps the unrelated edit saveable.
 *
 * ## NO materiality threshold here (DEV-1531)
 *
 * ANY drift re-derives, however small. The 15/30 rule is a churn control for the ENGINE, which wakes
 * repeatedly against a flight that moves all day and would otherwise nudge the same booking a dozen times,
 * each costing a driver re-confirm. An edit is one deliberate human save, so that failure does not exist on
 * this path — and the change-review modal discloses the move before the client confirms, which is the
 * consent step the engine never had. `flightUpdateIsMaterial` therefore stays in `flightPickup.ts` for the
 * engine's rule; only this caller drops it. Supersedes D4/D5 on DEV-1490 for the edit path.
 *
 * ## Committing it is the CALLER's move, on save
 *
 * `applyRederivedPickup` is dispatched from the "Update Booking" click, before the change-review modal
 * renders — see `PricedBookingSummary`. That ordering is load-bearing three ways: the diff dialog gets to
 * disclose `Pickup Time: <old> → <new>` before the client confirms; `pickup_datetime` is in the payload
 * before the server's own `hasPricingRelevantFieldsChanged()` runs, so the booking reprices with no second
 * pricing call; and the CTA's enablement is untouched, so opening a booking and leaving it never looks like
 * an unsaved change.
 *
 * `delayMinutes` normalises a never-asked null to 0, matching `DateTimePanel`'s `delayMin`. Not cosmetic:
 * `buildBookingPayload` drops `flight_landing_used` when the delay is null, so leaving it null would cost
 * the re-derived pickup its provenance and let the server resolve an arrival of its own.
 */
export const useSheetFlightPickup = ({
  syncPickupTerminal = false,
}: {
  /**
   * Drive the arrival-terminal swap from this instance (DEV-1594) — see {@link useTerminalSwap}.
   *
   * Opt-in because the hook is mounted TWICE: once for the sheet's lifetime from `ClientBooking`, and
   * again inside `PricedBookingSummary` for its Update-Booking handler. Everything else here is a memo or
   * a callback and so is free to run in both, but a dispatching effect is not: two instances would each
   * fire the swap off the same resolved flight, and the Google fallback is a billed request. Only the
   * sheet-lifetime mount passes this.
   */
  syncPickupTerminal?: boolean;
} = {}): {
  /** The triple to commit, or null when the pickup must stay exactly as booked. */
  rederived: RederivedFlightPickup | null;
  /** Commit the triple. A no-op when there is nothing to re-derive, so callers need no guard. */
  applyRederivedPickup: () => void;
  /**
   * Put the booked triple back, for a client who saw the disclosure and declined it. A no-op when nothing
   * was applied, so callers need no guard.
   */
  revertRederivedPickup: () => void;
} => {
  const dispatch = useAppDispatch();
  const pickup = useAppSelector(selectPickup);
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const committed = useAppSelector(selectPickupDatetime);
  const committedDelay = useAppSelector(selectPickupFlightDelayMinutes);
  const derivedArrival = useAppSelector(selectPickupFlightDerivedArrival);
  const frozenReason = useAppSelector(selectPickupFlightFrozenReason);
  const committedLandingUsed = useAppSelector(selectPickupFlightLandingUsed);
  const viewOnly = useAppSelector(selectViewOnly);

  const delayMin = committedDelay ?? 0;
  const frozen = isFlightPickupFrozen(frozenReason);
  const flightDate = useMemo(
    () => (committed && pickupFlightNumber ? flightDateFromPickup(committed, committedDelay) : null),
    [committed, pickupFlightNumber, committedDelay]
  );

  // Skipped for a frozen booking as well as a closed one: nothing here renders the flight, so a lookup that
  // can never re-derive has no consumer. The date panel still fetches when it is opened, which is what keeps
  // this from changing the request count on any existing flow.
  const { data: flight, isError } = useFlightDetails({
    flightNumber: pickupFlightNumber ?? "",
    date: flightDate ?? "",
    airport: extractAirportIata(pickup) ?? undefined,
    skip: !pickupFlightNumber || !flightDate || viewOnly || frozen,
  });

  // Deliberately NOT gated on `isFetching`, which is also true while a mount-triggered refetch is in
  // flight over perfectly good data. Gating on it meant a client who opened the tablet/mobile slide-in
  // summary and tapped Update inside that window saved the booked pickup with nothing disclosed — a
  // silent no-op indistinguishable from "the flight has not moved". `flight` is undefined until the first
  // lookup resolves, so the initial window is closed by construction rather than by a flag; `DateTimePanel`
  // needs `isFetching` because it RENDERS the landing and must not show one mid-refetch.
  const landing = !isError && flightWillArrive(flight) ? landingTime(flight) : null;

  // Live GATE landing against the one the booked pickup was built on — both gate, so they are the same
  // quantity measured at two times. The stored value is UTC while every other flight time here carries the
  // airport's own offset, so it is converted through the arrival airport's timezone rather than read as a
  // wall clock (a Sydney arrival would otherwise render ten hours early).
  const storedLandingIso = useMemo(() => {
    const zone = flight?.arrival?.airport?.timezone;
    return derivedArrival && zone ? utcIsoToAirportIso(derivedArrival, zone) : null;
  }, [derivedArrival, flight?.arrival?.airport?.timezone]);

  const rederived = useMemo<RederivedFlightPickup | null>(() => {
    if (viewOnly || frozen) return null;
    if (!committed || !landing || !storedLandingIso) return null;
    if (Date.parse(landing) === Date.parse(storedLandingIso)) return null;
    if (validatePickup(pickupFromFlight(landing, delayMin))) return null;
    return {
      value: flightPickupCommitValue(landing, delayMin).toISOString(),
      delayMinutes: delayMin,
      // Built from the landing THIS lookup resolved, on the day `flightDate` asked about — so the day
      // looked up and the landing sent can never describe different flights.
      landingUsed: flightLandingUsedIso(landing),
    };
  }, [viewOnly, frozen, committed, landing, storedLandingIso, delayMin]);

  /**
   * The booked triple as it stood before the re-derivation was committed, held from the moment of apply so
   * a decline can put it back exactly.
   *
   * A ref rather than state: nothing renders from it, and re-rendering the sheet on apply would be a second
   * render for a value only the decline path reads.
   *
   * Only ever written when EMPTY, so it holds what the client actually booked rather than whatever the last
   * apply left behind. Applying twice without a decline in between — the modal is dismissed by a failed save
   * and reopened — must not promote a re-derived pickup into the thing a later decline restores.
   */
  const bookedTriple = useRef<{ value: string | null; delayMinutes: number | null; landingUsed: string | null } | null>(null);

  const applyRederivedPickup = useCallback(() => {
    if (!rederived || !landing) return;
    // Re-run the elapsed check against a FRESH now. `rederived` is memoised on flight and draft values, so
    // its own guard was evaluated when the flight last moved — which can be a long edit session before this
    // click. A pickup that was comfortably in the future when the sheet opened can be in the past by the
    // time someone finishes editing their notes, and committing it then hands them "Pickup time can't be in
    // the past" on an edit they did not make: exactly what the guard exists to prevent.
    if (validatePickup(pickupFromFlight(landing, delayMin))) return;
    if (!bookedTriple.current) {
      bookedTriple.current = { value: committed, delayMinutes: committedDelay, landingUsed: committedLandingUsed };
    }
    dispatch(setPickupDatetime(rederived.value));
    dispatch(setPickupFlightDelayMinutes(rederived.delayMinutes));
    dispatch(setPickupFlightLandingUsed(rederived.landingUsed));
  }, [dispatch, rederived, landing, delayMin, committed, committedDelay, committedLandingUsed]);

  /**
   * The pickup is committed BEFORE the client agrees to it, because that is what puts it in the diff they
   * read — the disclosure has to precede the confirmation. Declining therefore has to undo it, or the sheet
   * keeps a change the client just refused: the CTA stays enabled on a pickup nobody moved, and the price
   * summary stays flipped from the invoiced stored price to a live re-quote.
   *
   * All three go back together, for the same reason they are committed together. Restoring the pickup while
   * leaving the landing behind would hand the server `pickup != arrival + wait` on the next save.
   */
  const revertRederivedPickup = useCallback(() => {
    const booked = bookedTriple.current;
    if (!booked) return;
    bookedTriple.current = null;
    dispatch(setPickupDatetime(booked.value));
    dispatch(setPickupFlightDelayMinutes(booked.delayMinutes));
    dispatch(setPickupFlightLandingUsed(booked.landingUsed));
  }, [dispatch]);

  useTerminalSwap({
    // `isError` for the same reason `landing` above weighs it: RTK Query retains the last good `data`
    // through a failed refetch, and rewriting the pickup off an arrival the server has since stopped
    // confirming is worse than leaving it.
    enabled: syncPickupTerminal && !viewOnly && !frozen && !isError,
    flight,
    pickup,
    pickupFlightNumber,
  });

  return { rederived, applyRederivedPickup, revertRederivedPickup };
};

/**
 * Move the pickup to the terminal the flight actually arrives at, once the lookup says which (DEV-1594).
 *
 * A client booking an airport pickup picks "Sydney Airport" — or last month's terminal — and the terminal
 * is what the driver needs. It is knowable: the same lookup that times the car already carries
 * `arrival.terminal`. So rather than asking the client to know it, the sheet reads it and moves the pickup,
 * and the Booking Summary discloses the move.
 *
 * ## What it will not do
 *
 * Nothing runs without a terminal on the leg, and nothing runs when the pickup already names that terminal
 * at that airport — which is also what makes it idempotent, since the POI it swaps in satisfies exactly
 * that check. View-only and frozen bookings are excluded by the caller: a closed booking's pickup is not
 * ours to move, and a frozen one is the engine's.
 *
 * `flightWillArrive` gates it for the same reason the re-derivation does — a cancelled flight still
 * carries a terminal, and rewriting a pickup off a flight that is not coming is worse than leaving it.
 *
 * ## One attempt per pickup
 *
 * `attempted` holds the flight + terminal the last attempt was made for, cleared whenever the pickup
 * itself moves. A successful swap needs no guard (the match check closes it), but a MISS does: with no
 * provider row and no Google hit there is nothing to change, so without this the effect would re-ask on
 * every render the flight object is replaced on. Clearing it on a pickup change is what lets the swap run
 * again after the client edits the pickup by hand and re-enters the flight.
 */
const useTerminalSwap = ({
  enabled,
  flight,
  pickup,
  pickupFlightNumber,
}: {
  enabled: boolean;
  flight?: FlightDetails;
  pickup: PointsOfInterest | null;
  pickupFlightNumber: string | null;
}) => {
  const dispatch = useAppDispatch();
  const operatorId = useAppSelector(operatorIdSelector);
  const country = useAppSelector(defaultCountrySelector);
  // Same argument the address search subscribes with, so this shares that cache entry rather than
  // fetching the provider's POIs a second time.
  const {
    data: providerPois = NO_POIS,
    isLoading: poisLoading,
    isError: poisError,
    isUninitialized: poisUninitialized,
  } = useListProviderPoisQuery(undefined, { skip: !enabled || !operatorId });
  // NOT KNOWN vs known-empty. `isLoading` alone is false for an errored or not-yet-subscribed query and
  // `data` is then undefined, which reads as "the operator has no row for this terminal" and bills the
  // Google fallback over a row that may well exist. With no operator there are no provider POIs to wait
  // for, so that case is settled by construction.
  const providerPoisUnknown = !!operatorId && (poisLoading || poisError || poisUninitialized);
  const { isLoaded: googleLoaded } = useLoadScript({ googleMapsApiKey: getGoogleApiKey(), libraries });

  const attempted = useRef<string | null>(null);
  useEffect(() => {
    attempted.current = null;
  }, [pickup]);

  useEffect(() => {
    // Provider POIs rank ahead of Google, so an attempt made before they land could reach for Google
    // over a row that was about to arrive — and `attempted` would then hold that miss.
    if (!enabled || providerPoisUnknown || !pickupFlightNumber || !flightWillArrive(flight)) return;
    // An AIRPORT pickup only. A flight number is not proof the pickup is an airport: `viewDraft` hydrates
    // `pickupFlightNumber` from the stored `flight_number` whatever the pickup is, so opening a booking
    // with a hotel pickup and a flight on it would otherwise move that pickup to a terminal — the client
    // would find their pickup relocated to an airport by an edit they came to make somewhere else.
    if (!isAirportPickup(pickup)) return;
    const arrival: FlightArrivalTerminal = {
      iata: flight?.arrival?.airport?.iata ?? null,
      name: flight?.arrival?.airport?.name ?? null,
      terminal: flight?.arrival?.terminal ?? null,
    };
    const arrivalIso = landingTime(flight);
    if (!arrival.terminal || !arrival.iata || !arrivalIso) return;
    if (pickupIsAtFlightTerminal(pickup, arrival)) return;

    const key = `${pickupFlightNumber}|${arrival.iata}|${arrival.terminal}`;
    if (attempted.current === key) return;
    attempted.current = key;

    const source = { flightNumber: pickupFlightNumber, terminal: arrival.terminal, arrivalIso };
    const providerHit = findProviderTerminalPoi(arrival, providerPois);
    if (providerHit) {
      dispatch(swapPickupForTerminal({ pickup: providerHit, source }));
      return;
    }
    // No provider row for this terminal — fall through to Google, but only once its single loader is
    // ready. Re-asked rather than dropped: this attempt is released so the effect runs again on the
    // render that reports the loader up.
    if (!googleLoaded) {
      attempted.current = null;
      return;
    }
    let cancelled = false;
    let settled = false;
    findGoogleTerminalPoi(arrival, country).then((poi) => {
      settled = true;
      if (!cancelled && poi) dispatch(swapPickupForTerminal({ pickup: poi, source }));
    });
    return () => {
      cancelled = true;
      // A lookup cut short by a re-run has not answered yet, so the attempt is released and asked again.
      // Holding the key here loses the swap entirely: the flight query refetches whenever a subscriber
      // mounts onto a stale entry (opening the Date & Time step), which re-runs this effect straight
      // through an in-flight Places call.
      if (!settled) attempted.current = null;
    };
  }, [enabled, providerPoisUnknown, flight, pickup, pickupFlightNumber, providerPois, googleLoaded, country, dispatch]);
};
